'use client';

import { sellerTypeOptions, sortingOptions } from '@c/ProductsPage';
import ListingFilterControls from '@c/filters/ListingFilterControls';
import { ArrowsUpDownIcon } from '@heroicons/react/20/solid';
import ButtonToggle from '@ui/ButtonToggle';
import Input from '@ui/Input';
import Select from '@ui/Select';
import { PageSideFilters, TopFilters } from '@util/hooks/useProductsPage';
import { ButtonToggleType } from '@util/types/buttontoggle';
import { ChevronDown, ChevronsUpDown, ListFilter } from 'lucide-react';
import { CategoryDocument } from 'models/category';
import {
  Condition,
  FacetCounts,
  isVehicleCategory,
  ProductSortOption,
  VEHICLE_CATEGORIES,
} from 'models/product';
import { SellerType } from 'models/shared';
import { MotionDiv } from 'motion';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import MXDirectFilterPill from '@c/products/MXDirectFilterPill';
import MobileSearchFilters from './MobileSearchFilters';
import MobileSearchSort from './MobileSearchSort';

type SortingOption = {
  label: string;
  value: ProductSortOption;
  id: string;
  icon: JSX.Element;
};

type Props = {
  handleConditionsChange: (filters: Condition[]) => void;
  handleSellerTypeChange: (filters: SellerType[]) => void;
  handleSortChange: (option: SortingOption) => void;
  handleSearchChange: (value: string) => void;
  handleSaleChange: (value: boolean) => void;
  setDrawerOpen: (open: boolean) => void;
  setDropdownOpen: (open: boolean) => void;
  topFilters: TopFilters;
  showSearch: boolean | undefined;
  pageType: string;
  dropdownOpen: boolean;
  buttonToggles: ButtonToggleType[];
  filterState: PageSideFilters;
  setFilterState: (filterState: PageSideFilters) => void;
  currentCat: CategoryDocument | null;
  facet_counts: FacetCounts | undefined;
  keyword?: string;
  mostly_parts?: boolean;
  categories: CategoryDocument[];
};

export default function SearchControls({
  handleConditionsChange,
  handleSellerTypeChange,
  handleSortChange,
  handleSearchChange,
  setDropdownOpen,
  topFilters,
  showSearch,
  pageType,
  dropdownOpen,
  buttonToggles,
  filterState,
  setFilterState,
  facet_counts,
  keyword,
  mostly_parts,
  categories,
}: Props) {
  const [showFilters, setShowFilters] = useState(false);
  const [filtersInitialPage, setFiltersInitialPage] = useState<
    'main' | 'categories'
  >('main');
  const [showSort, setShowSort] = useState(false);
  const pathname = usePathname();

  const getCurrentCondition = () => {
    if (filterState.conditions.length === 0) return 'all';
    if (filterState.conditions.includes('New')) return 'new';
    return 'used';
  };

  const isSellerProfilePage = pathname?.includes('/profile');
  const isDirtBikesPage =
    pathname?.includes('-for-sale') ||
    isVehicleCategory(filterState.product_cats?.category) ||
    VEHICLE_CATEGORIES.some((c) => filterState.category?.includes(c));
  const isSalePage = pathname?.includes('/shop/sale');
  const showMXDirectFilter =
    !isSellerProfilePage && !isDirtBikesPage && !isSalePage;
  const hasFilters =
    filterState.conditions.length > 0 ||
    filterState.brands.length > 0 ||
    (filterState.category && filterState.category.length > 0) ||
    (filterState.product_cats?.category &&
      filterState.product_cats.category.length > 0) ||
    (filterState.product_cats?.category1 &&
      filterState.product_cats.category1.length > 0) ||
    (filterState.product_cats?.category2 &&
      filterState.product_cats.category2.length > 0) ||
    (filterState.product_cats?.category3 &&
      filterState.product_cats.category3.length > 0) ||
    (filterState.letterSizes && filterState.letterSizes.length > 0) ||
    (filterState.numberSizes && filterState.numberSizes.length > 0) ||
    (filterState.sellerType && filterState.sellerType.length > 0);

  return (
    <div className="flex w-full flex-col gap-y-4 pl-2 pr-8">
      <div className={'flex w-full flex-col gap-y-4 px-2 sm:hidden'}>
        {showSearch && (
          <div className="mb-6 mt-4 flex-1">
            <Input
              type="search"
              placeholder={
                pageType === 'profile'
                  ? 'Search this store...'
                  : 'Search anything...'
              }
              onChange={(e) => {
                handleSearchChange(e.target.value);
              }}
              initialValue={keyword}
              className="!h-[4.5rem] w-full !rounded-[2rem] border border-zinc-200 !py-0 !pr-0"
              searchIconRight
            />
          </div>
        )}
        <div className="-mx-2 flex w-full items-center gap-x-3 overflow-x-auto px-2 pb-2 scrollbar-none">
          {showMXDirectFilter && (
            <MXDirectFilterPill
              enabled={filterState.mxDirect === true}
              onChange={(enabled) =>
                setFilterState({ ...filterState, mxDirect: enabled })
              }
            />
          )}

          <button
            type="button"
            className="relative flex h-[4.5rem] shrink-0 items-center gap-x-2 rounded-full bg-[#F1F1F1] px-5"
            onClick={() => {
              setFiltersInitialPage('main');
              setShowFilters(true);
            }}
          >
            {hasFilters && (
              <div className="absolute right-3 top-2 h-3 w-3 rounded-full bg-brand-secondary" />
            )}
            <ListFilter className="h-6 w-6 text-black" />
            <span className="text-[1.6rem] font-medium text-black">Filter</span>
          </button>

          <button
            type="button"
            className="flex h-[4.5rem] shrink-0 items-center gap-x-2 rounded-full bg-[#F1F1F1] px-5"
            onClick={() => setShowSort(true)}
          >
            <ChevronsUpDown className="h-6 w-6 text-black" />
            <span className="text-[1.6rem] font-medium text-black">Sort</span>
          </button>

          <button
            type="button"
            className="flex h-[4.5rem] shrink-0 items-center gap-x-2 rounded-full bg-[#F1F1F1] px-5"
            onClick={() => {
              setFiltersInitialPage('categories');
              setShowFilters(true);
            }}
          >
            <span className="text-[1.6rem] font-medium text-black">
              Categories
            </span>
            <ChevronDown className="h-6 w-6 text-black" />
          </button>

          <div className="shrink-0 overflow-hidden rounded-full">
            <ButtonToggle buttons={buttonToggles} />
          </div>
        </div>

        {/* Mobile search input */}

        <MobileSearchFilters
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          categories={categories}
          filterState={filterState}
          setFilterState={setFilterState}
          facet_counts={facet_counts}
          mostly_parts={mostly_parts}
          pageType={pageType}
          initialPage={filtersInitialPage}
        />

        <MobileSearchSort
          show={showSort}
          setShow={setShowSort}
          sortingBy={topFilters.sort}
          onSortingChange={handleSortChange}
          pageType={pageType}
          filterState={filterState}
        />
      </div>
      <div className="hidden w-full flex-row items-center justify-between gap-[2.4rem] sm:flex sm:items-start sm:px-0">
        <ListingFilterControls
          condition={getCurrentCondition()}
          onConditionChange={(value) => {
            if (value === 'all') handleConditionsChange([]);
            else if (value === 'new') handleConditionsChange(['New']);
            else handleConditionsChange(['Used']);
          }}
          showSeller={!isSellerProfilePage}
          sellerPlaceholder="Seller Type"
          sellerValue={filterState.sellerType?.[0]?.toLowerCase() ?? ''}
          sellerOptions={sellerTypeOptions.map((option) => ({
            id: option.key,
            label: option.label,
            value: option.value,
          }))}
          onSellerChange={(value) =>
            handleSellerTypeChange([value as SellerType])
          }
        />

        <div className="flex w-full flex-col items-end gap-4 sm:flex-row sm:items-center sm:justify-end">
          {showSearch && (
            <div className="hidden sm:block">
              <Input
                type="search"
                placeholder="Search anything..."
                ariaLabel="search input"
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                initialValue={keyword}
                className="!h-[3.8rem] !rounded-[2rem] border border-zinc-200"
              />
            </div>
          )}
          <div className="hidden sm:block">
            <Select
              value={topFilters.sort}
              options={sortingOptions
                .filter((option) => {
                  if (pageType === 'profile') {
                    return option.id !== 'soldDate';
                  } else if (pageType === 'recent') {
                    return option.id !== 'bestMatch';
                  } else if (!filterState.recentlySold) {
                    return option.id !== 'soldDate';
                  }
                  return true;
                })
                .map((option) => ({
                  label: option.label,
                  value: option.id,
                  id: option.id,
                }))}
              onChange={(option) => {
                handleSortChange(option as SortingOption);
              }}
              allowEmpty={false}
              className="!h-[4.5rem] min-w-[18rem] !rounded-[1.5rem] !border-none !bg-[#F1F1F1]"
              rounded
            />
          </div>

          <div className="flex items-center gap-x-2 ">
            <div className="sm:hidden">
              <button
                type="button"
                className="flex h-[3.5rem] w-[3.5rem] items-center justify-center rounded-full border border-zinc-200 p-2"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-label="Sort"
              >
                <ArrowsUpDownIcon width={44} height={44} />
              </button>

              {dropdownOpen && (
                <MotionDiv
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-[10rem] z-[90] mt-2 flex w-[20rem] flex-col gap-y-2 rounded-md bg-white shadow-lg sm:right-0"
                >
                  {sortingOptions
                    .filter((option) => {
                      if (pageType === 'profile') {
                        return option.id !== 'soldDate';
                      }
                      return true;
                    })
                    .map((option) => (
                      <button
                        type="button"
                        key={option.id}
                        className="flex w-full items-center gap-x-2 px-4 py-2 text-left text-black hover:bg-gray-100"
                        onClick={() => {
                          handleSortChange(option);
                          setDropdownOpen(false);
                        }}
                        aria-label={option.label}
                      >
                        {option.icon} {option.label}
                      </button>
                    ))}
                </MotionDiv>
              )}
            </div>
            <div className="hidden sm:block">
              <ButtonToggle buttons={buttonToggles} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
